/**
 * OmniSearch — couche API
 *
 * Regroupe tous les appels de recherche cross-entité.
 * Chaque fonction accepte un signal AbortController pour annuler la requête
 * dès que l'utilisateur tape à nouveau.
 */
import { apiFetch } from '@/auth/apiFetch'

// ─── Helpers ────────────────────────────────────────────────────────────────

function qs(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  )
  if (!entries.length) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of entries) usp.set(k, String(v))
  return `?${usp.toString()}`
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

const JSON_HEADERS = { 'Content-Type': 'application/json' }

// ─── Types résultats ─────────────────────────────────────────────────────────

export interface OsQuoteResult {
  uuid: string
  quoteReference: string
  customerReference: string | null
  order: { uuid: string } | null
  regulations: { uuid: string }[]
}

export interface OsOrderResult {
  uuid: string
  comments: string | null
  status: string
  orderComments: { uuid: string; comment: string }[]
  quote: {
    uuid: string
    quoteReference: string
    company: { uuid: string; name: string; referenceExterne: string | null } | null
  } | null
}

export interface OsInvoiceResult {
  uuid: string
  name: string | null
  company: { uuid: string; name: string | null } | null
}

export interface OsServiceResult {
  uuid: string
  name: string | null
  externalReference: string | null
  type: string | null
}

export interface OsCompanyResult {
  uuid: string
  name: string | null
  siren: string | null
  siret: string | null
  vat: string | null
  zip: string | null
  referenceExterne: string | null
}

export interface OsContactResult {
  uuid: string
  firstname: string | null
  lastname: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  company: { uuid: string; name: string | null } | null
}

export interface OsLeadResult {
  uuid: string
  companyName: string | null
  contactFirstname: string | null
  contactLastname: string | null
  contactEmail: string | null
  companySiret: string | null
  status: string
}

export interface OsTicketResult {
  uuid: string
  title: string
  description: string | null
  internalReference: number | null
  status: string
}

export interface OsCampaignResult {
  uuid: string
  name: string
  status: string | null
}

export interface OsEligibilityResult {
  uuid: string
  fulladdress: string | null
  codeBatiment: string | null
  city: string | null
  zip: string | null
  company: {
    uuid: string
    name: string | null
    siren: string | null
    siret: string | null
  } | null
}

export interface OsOpportunityResult {
  id: number
  name: string
  partnerId: number | null
  companyUuid: string | null
  partnerName: string | null
  partnerSiret: string | null
  partnerSiren: string | null
  stageName: string | null
  expectedRevenue: number
  dateDeadline: string | null
}

export interface OmnisearchResults {
  quotes: OsQuoteResult[]
  orders: OsOrderResult[]
  invoices: OsInvoiceResult[]
  services: OsServiceResult[]
  companies: OsCompanyResult[]
  contacts: OsContactResult[]
  leads: OsLeadResult[]
  tickets: OsTicketResult[]
  campaigns: OsCampaignResult[]
  eligibilities: OsEligibilityResult[]
  opportunities: OsOpportunityResult[]
}

export type OmnisearchEntityKey = keyof OmnisearchResults

/** Union discriminante : permet au template TypeScript de narrower `section.items` par `section.key` */
export type TypedSection =
  | { key: 'companies';     label: string; items: OsCompanyResult[]     }
  | { key: 'contacts';      label: string; items: OsContactResult[]      }
  | { key: 'orders';        label: string; items: OsOrderResult[]        }
  | { key: 'quotes';        label: string; items: OsQuoteResult[]        }
  | { key: 'leads';         label: string; items: OsLeadResult[]         }
  | { key: 'tickets';       label: string; items: OsTicketResult[]       }
  | { key: 'invoices';      label: string; items: OsInvoiceResult[]      }
  | { key: 'services';      label: string; items: OsServiceResult[]      }
  | { key: 'campaigns';     label: string; items: OsCampaignResult[]     }
  | { key: 'eligibilities'; label: string; items: OsEligibilityResult[]  }
  | { key: 'opportunities'; label: string; items: OsOpportunityResult[]  }

// ─── Appels individuels ───────────────────────────────────────────────────────

function searchQuotes(q: string, limit: number, signal?: AbortSignal): Promise<OsQuoteResult[]> {
  return apiFetch(`/quote/search${qs({ q, limit })}`, { signal }).then(json<OsQuoteResult[]>)
}

function searchOrders(q: string, limit: number, signal?: AbortSignal): Promise<OsOrderResult[]> {
  return apiFetch(`/orders/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsOrderResult[]>)
}

function searchInvoices(q: string, limit: number, signal?: AbortSignal): Promise<OsInvoiceResult[]> {
  return apiFetch(`/invoices/search${qs({ q, limit })}`, { signal }).then(json<OsInvoiceResult[]>)
}

function searchServices(q: string, limit: number, signal?: AbortSignal): Promise<OsServiceResult[]> {
  // Endpoint omnisearch dédié : payload minimal, sans count ni enrichissement santé
  return apiFetch(`/service/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsServiceResult[]>)
}

function searchCompanies(q: string, limit: number, signal?: AbortSignal): Promise<OsCompanyResult[]> {
  // Endpoint omnisearch dédié : payload minimal, sans count
  return apiFetch(`/companies/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsCompanyResult[]>)
}

function searchContacts(q: string, limit: number, signal?: AbortSignal): Promise<OsContactResult[]> {
  // Endpoint omnisearch dédié : payload minimal, sans count
  return apiFetch(`/contacts/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsContactResult[]>)
}

function searchLeads(q: string, limit: number, signal?: AbortSignal): Promise<OsLeadResult[]> {
  return apiFetch(`/leads/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsLeadResult[]>)
}

function searchTickets(q: string, limit: number, signal?: AbortSignal): Promise<OsTicketResult[]> {
  return apiFetch(`/tickets${qs({ search: q, limit })}`, { signal }).then(json<OsTicketResult[]>)
}

function searchCampaigns(q: string, limit: number, signal?: AbortSignal): Promise<{ data: OsCampaignResult[]; total: number }> {
  return apiFetch(`/campaigns/private${qs({ q, limit })}`, { signal }).then(json<{ data: OsCampaignResult[]; total: number }>)
}

function searchEligibilities(q: string, limit: number, signal?: AbortSignal): Promise<OsEligibilityResult[]> {
  // /eligibility retourne un tableau direct (pas { data: [] })
  return apiFetch('/eligibility', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ search: q, limit, offset: 0 }),
    signal,
  }).then(json<OsEligibilityResult[]>)
}

function searchOpportunities(q: string, limit: number, signal?: AbortSignal): Promise<OsOpportunityResult[]> {
  // Proxy boostcore → connector Odoo (crm.lead name ilike, type=opportunity)
  return apiFetch(`/opportunity/omnisearch${qs({ q, limit })}`, { signal }).then(json<OsOpportunityResult[]>)
}

// ─── Appel parallèle global ───────────────────────────────────────────────────

export async function searchAll(
  q: string,
  limit: number,
  signal?: AbortSignal,
): Promise<OmnisearchResults> {
  const [
    quotesRes,
    ordersRes,
    invoicesRes,
    servicesRes,
    companiesRes,
    contactsRes,
    leadsRes,
    ticketsRes,
    campaignsRes,
    eligibilitiesRes,
    opportunitiesRes,
  ] = await Promise.allSettled([
    searchQuotes(q, limit, signal),
    searchOrders(q, limit, signal),
    searchInvoices(q, limit, signal),
    searchServices(q, limit, signal),
    searchCompanies(q, limit, signal),
    searchContacts(q, limit, signal),
    searchLeads(q, limit, signal),
    searchTickets(q, limit, signal),
    searchCampaigns(q, limit, signal),
    searchEligibilities(q, limit, signal),
    searchOpportunities(q, limit, signal),
  ])

  function val<T>(r: PromiseSettledResult<T>, fallback: T): T {
    return r.status === 'fulfilled' ? r.value : fallback
  }

  return {
    quotes:        val(quotesRes, []),
    orders:        val(ordersRes, []),
    invoices:      val(invoicesRes, []),
    services:      val(servicesRes, []),
    companies:     val(companiesRes, []),
    contacts:      val(contactsRes, []),
    leads:         val(leadsRes, []),
    tickets:       val(ticketsRes, []),         // tableau direct
    campaigns:     (val(campaignsRes, { data: [], total: 0 }) as { data: OsCampaignResult[] }).data ?? [],
    eligibilities: val(eligibilitiesRes, []),   // tableau direct
    opportunities: val(opportunitiesRes, []),
  }
}
